/**
 * VideoSessionContext holds Blob URLs for videos uploaded in the current browser session.
 *
 * Athlete videos on the server are ephemeral — deleted after processing. This context
 * bridges that gap by keeping an in-memory Map of videoId → Blob URL and persisting
 * the underlying File objects to IndexedDB for survival across page reloads.
 *
 * FUTURE: If the SPORTFORM_RETAIN_ALL_VIDEOS flag is ever enabled (server-side permanent
 * storage), this layer remains useful for faster local playback and reduced server egress.
 * The hook useVideoSource already implements the 4-level fallback that degrades to server
 * URL, so enabling server retention requires no changes here.
 */

import { createContext, useContext } from 'react';

interface VideoSessionContextValue {
  /**
   * Register an uploaded athlete video File. Creates a Blob URL immediately for
   * in-session playback and asynchronously persists the File to IndexedDB so it
   * survives page reloads within the 24-hour TTL.
   */
  registerUploadedFile: (videoId: string, file: File) => void;
  /**
   * Return the Blob URL for a previously registered video, or null if not found
   * in the in-memory session map. For cross-reload lookup, use useVideoSource which
   * also checks IndexedDB.
   */
  getBlobUrl: (videoId: string) => string | null;
}

// Exported so VideoSessionProvider.tsx (react-refresh/only-export-components
// split — see that file's comment) can construct the Provider.
export const VideoSessionContext = createContext<VideoSessionContextValue | undefined>(undefined);

export function useVideoSession(): VideoSessionContextValue {
  const ctx = useContext(VideoSessionContext);
  if (!ctx) throw new Error('useVideoSession must be used within VideoSessionProvider');
  return ctx;
}
